import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { projectFormSchema, type ProjectFormData } from '@/schemas/projectSchemas';
import type { Project, Customer } from '@/types/legacy';
import { Loader2, Hash, AlertTriangle } from 'lucide-react';
import logger from '@/lib/logger';

interface ProjectFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: Project | null;
  onSuccess?: () => void;
}

export function ProjectFormDialog({ open, onOpenChange, project, onSuccess }: ProjectFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [nextProjectNumber, setNextProjectNumber] = useState<number | null>(null);

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    values: {
      name: project?.name || '',
      reference_code: project?.reference_code || '',
      customer_id: project?.customer_id || '',
      description: project?.description || '',
      status: project?.status || 'planning',
      start_date: project?.start_date || '',
      end_date: project?.end_date || '',
      estimated_hours: project?.estimated_hours,
      actual_hours: project?.actual_hours || 0,
      budget: project?.budget,
      notes: project?.notes || '',
      document_url: project?.document_url || '',
      is_active: project?.is_active ?? true,
    },
  });

  useEffect(() => {
    if (open) {
      fetchCustomers();
      // Fetch next project number (universal sequence)
      if (!project) {
        api.from('projects')
          .select('project_number')
          .order('project_number', { ascending: false })
          .limit(1)
          .then(({ data }) => {
            const maxNum = data?.[0]?.project_number || 0;
            setNextProjectNumber(maxNum + 1);
          });
      }
    }
  }, [open, project]);

  const fetchCustomers = async () => {
    try {
      const { data, error } = await api
        .from('customers')
        .select('id,first_name,last_name,company_name,code')
        .eq('is_active', true)
        .order('company_name');

      if (error) throw error;
      setCustomers(data || []);
    } catch (error: any) {
      logger.error('Error fetching customers:', error);
    }
  };

  // Sugerir reference_code = CODCLIENTE-NNN correlativo por cliente (editable)
  const suggestReferenceCode = async (customerId: string) => {
    try {
      const cust: any = customers.find((c: any) => c.id === customerId);
      const code = cust?.code;
      if (!code) return;
      const { data } = await api.from('projects').select('reference_code').eq('customer_id', customerId);
      const re = new RegExp('^' + String(code).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '-(\\d+)$');
      let max = 0;
      (data || []).forEach((r: any) => { const m = (r.reference_code || '').match(re); if (m) max = Math.max(max, parseInt(m[1], 10)); });
      form.setValue('reference_code', `${code}-${String(max + 1).padStart(3, '0')}`);
    } catch { /* noop */ }
  };

  const onSubmit = async (data: ProjectFormData) => {
    setLoading(true);
    try {
      // Validar unicidad del reference_code
      if (data.reference_code) {
        const { data: dup } = await api.from('projects').select('id').eq('reference_code', data.reference_code);
        const conflict = (dup || []).some((r: any) => r.id !== project?.id);
        if (conflict) {
          toast({ title: 'Código duplicado', description: `El código "${data.reference_code}" ya existe. Elige otro.`, variant: 'destructive' });
          setLoading(false);
          return;
        }
      }
      const payload = {
        name: data.name,
        reference_code: data.reference_code || null,
        customer_id: data.customer_id || null,
        description: data.description || null,
        status: data.status,
        start_date: data.start_date || null,
        end_date: data.end_date || null,
        estimated_hours: data.estimated_hours || null,
        actual_hours: data.actual_hours,
        budget: data.budget || null,
        notes: data.notes || null,
        document_url: data.document_url || null,
        is_active: data.is_active,
      };

      if (project) {
        const { error } = await api
          .from('projects')
          .update(payload)
          .eq('id', project.id);

        if (error) throw error;

        toast({
          title: 'Proyecto actualizado',
          description: 'El proyecto se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('projects')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Proyecto creado',
          description: 'El proyecto se creó correctamente',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el proyecto',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>{project ? 'Editar Proyecto' : 'Nuevo Proyecto'}</DialogTitle>
            <Badge variant="outline" className="gap-1 font-mono bg-primary/10">
              P-{project ? String((project as any).project_number).padStart(4, '0') : nextProjectNumber ? String(nextProjectNumber).padStart(4, '0') : '....'}
            </Badge>
          </div>
          {!project && nextProjectNumber && (
            <DialogDescription className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Se asignará el número <strong>P-{String(nextProjectNumber).padStart(4, '0')}</strong> (correlativo universal)
            </DialogDescription>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Nombre del proyecto" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reference_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Código de Referencia</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Se asigna al elegir cliente" />
                    </FormControl>
                    <FormDescription>Autogenerado (CLIENTE-NNN); editable. Debe ser único.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="customer_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cliente</FormLabel>
                    <Select onValueChange={(v) => { field.onChange(v); if (!project) suggestReferenceCode(v); }} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar cliente" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="planning">Planificación</SelectItem>
                        <SelectItem value="active">Activo</SelectItem>
                        <SelectItem value="paused">Pausado</SelectItem>
                        <SelectItem value="completed">Completado</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Descripción del proyecto" rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha de Inicio</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha de Fin</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="estimated_hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Horas Estimadas</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        step="0.5"
                        min={0}
                        placeholder="0.0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="actual_hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Horas Reales</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 0)}
                        step="0.5"
                        min={0}
                        placeholder="0.0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="budget"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Presupuesto</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        step="0.01"
                        min={0}
                        placeholder="0.00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="document_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL del Documento</FormLabel>
                  <FormControl>
                    <Input {...field} type="url" placeholder="https://ejemplo.com/documento.pdf" />
                  </FormControl>
                  <FormDescription>
                    Enlace a documentación del proyecto
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Notas adicionales" rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      El proyecto está activo en el sistema
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {project ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
